import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const buildDisponibilidad = (promo = {}) => {
  const capacidad = Number(promo.capacidad);
  const capacidadValida = !Number.isNaN(capacidad) ? capacidad : null;

  const ocupadosRaw = promo.matriculados ?? 0;
  const ocupados = Number.isNaN(Number(ocupadosRaw)) ? 0 : Number(ocupadosRaw);

  const cuposRaw = promo.cuposDisponibles ?? promo.cupos_disponibles;
  const cuposParsed = cuposRaw === null || cuposRaw === undefined ? null : Number(cuposRaw);
  const cuposDisponibles = Number.isNaN(cuposParsed)
    ? null
    : cuposParsed !== null
      ? cuposParsed
      : capacidadValida !== null
        ? Math.max(capacidadValida - ocupados, 0)
        : null;

  return {
    capacidad: capacidadValida,
    ocupados,
    disponibles: cuposDisponibles,
  };
};

export default function PromocionesTableCard({
  promociones = [],
  loading,
  listError,
  reload,
  estadoOptions,
  formatDate,
  estadoBadgeClass,
  activoBadgeClass,
  canManagePromociones,
  canViewDetallePromocion,
  canUpdatePromocion,
  canDeletePromocion,
  abrirModalEditar,
  abrirModalEliminar,
}) {
  const minRows = 10;
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  if (loading) {
    return (
      <div className="management-card promociones-table-card text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="management-card management-alert d-flex align-items-center justify-content-between mb-4">
        <div className="me-3">
          <p className="mb-1">{listError}</p>
          <small className="text-muted">No se pudo obtener las promociones.</small>
        </div>
        {reload && (
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reload}>
              <i className="bi bi-arrow-repeat me-1" />
              Reintentar
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!promociones.length) {
    return (
      <div className="management-card promociones-empty text-center py-5">
        <i className="bi bi-inboxes promociones-empty-icon d-block mb-3" />
        <h5 className="fw-semibold mb-1">Aun no hay promociones registradas</h5>
        <p className="text-muted mb-3">Crea la primera promocion para comenzar a matricular estudiantes.</p>
        {reload && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>
    );
  }

  const emptyRows = Math.max(0, minRows - promociones.length);

  return (
    <div className="management-card promociones-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Listado</p>
          <h5 className="mb-0">Promociones registradas</h5>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={reload}
          disabled={loading}
        >
          <i className="bi bi-arrow-repeat me-2" />
          Actualizar
        </button>
      </div>
      <div className="management-table-wrapper">
        <table className="table align-middle management-table promociones-table mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>Promocion</th>
              <th>Curso / Convocatoria</th>
              <th>Fechas</th>
              <th>Disponibilidad</th>
              <th>Estado</th>
              <th>Cronograma</th>
              <th>Activa</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody ref={wrapRef}>
            {promociones.map((promo, index) => {
              const disponibilidad = buildDisponibilidad(promo);
              const capacidadLabel =
                disponibilidad.capacidad !== null && !Number.isNaN(disponibilidad.capacidad)
                  ? disponibilidad.capacidad
                  : "-";
              const disponiblesLabel =
                disponibilidad.disponibles === null || Number.isNaN(disponibilidad.disponibles)
                  ? "Cupos sin limite definido"
                  : `${disponibilidad.disponibles} cupos libres`;
              const isMenuOpen = openMenuId === promo.id;
              const irAInscritos = () =>
                canViewDetallePromocion && navigate(`/dashboard/coordinacion/promociones/${promo.id}/inscritos`);

              return (
                <tr key={promo.id}>
                  <td>{index + 1}</td>
                  <td
                    className="fw-semibold"
                    onClick={irAInscritos}
                    style={canViewDetallePromocion ? { cursor: "pointer" } : undefined}
                    title={canViewDetallePromocion ? "Ver inscritos de la promocion" : undefined}
                  >
                    {promo.nombre}
                  </td>
                  <td
                    onClick={irAInscritos}
                    style={canViewDetallePromocion ? { cursor: "pointer" } : undefined}
                    title={canViewDetallePromocion ? "Ver inscritos de la promocion" : undefined}
                  >
                    <div>{promo.cursoNombre || "-"}</div>
                    <small className="text-muted">{promo.codigoConvocatoria || (promo.cicloCodigo ? `${promo.cicloCodigo}${promo.cicloAnio ? ` · ${promo.cicloAnio}` : ""}` : "-")}</small>
                  </td>
                  <td>
                    <div className="small">
                      <div>
                        <i className="bi bi-calendar-event me-1" />
                        {formatDate(promo.fechaInicio)}
                      </div>
                      <div className="text-muted">
                        <i className="bi bi-flag me-1" />
                        {formatDate(promo.fechaFin)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <div className="fw-semibold">
                          {disponibilidad.ocupados}
                          {capacidadLabel === "-" ? "" : ` / ${capacidadLabel}`}
                        </div>
                        <small className="text-muted">{disponiblesLabel}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge rounded-pill px-3 py-2 ${estadoBadgeClass(promo.estado)}`}>
                      {(estadoOptions.find((opt) => opt.value === promo.estado)?.label) ||
                        (promo.estado || "Programado")}
                    </span>
                  </td>
                  <td
                    className="text-center"
                    style={canViewDetallePromocion ? { cursor: "pointer" } : undefined}
                    onClick={() =>
                      canViewDetallePromocion &&
                      navigate(`/dashboard/coordinacion/promociones/${promo.id}/inscritos`, { state: { tab: "cronograma" } })
                    }
                  >
                    {promo.totalSesiones > 0 ? (
                      <i className="bi bi-calendar2-check-fill text-success fs-5" title="Cronograma creado — clic para ver" />
                    ) : (
                      <i className="bi bi-exclamation-triangle-fill text-warning fs-5" title="Sin cronograma creado — clic para crear" />
                    )}
                  </td>
                  <td>
                    <span className={`badge rounded-pill px-3 py-2 ${activoBadgeClass(promo.activo)}`}>
                      {promo.activo ? "Activa" : "Inactiva"}
                    </span>
                  </td>
                  <td className="text-end">
                    <div className="management-actions-wrap">
                      <button
                        type="button"
                        className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isMenuOpen) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setOpenMenuId(isMenuOpen ? null : promo.id);
                          }}
                        title="Acciones"
                        aria-expanded={isMenuOpen}
                      >
                        <i className="bi bi-three-dots-vertical" />
                      </button>
                      {isMenuOpen && (
                        <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                          {canViewDetallePromocion && (
                            <li>
                              <button
                                type="button"
                                className="menu-item"
                                onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); navigate(`/dashboard/coordinacion/promociones/${promo.id}/inscritos`); }}
                              >
                                <i className="bi bi-people" />
                                Ver inscritos
                              </button>
                            </li>
                          )}
                          <li>
                            <button
                              type="button"
                              className="menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                navigate(`/dashboard/coordinacion/promociones/${promo.id}/inscritos`, { state: { tab: "cronograma" } });
                              }}
                            >
                              <i className="bi bi-grid-3x3" />
                              Ver cronograma
                            </button>
                          </li>
                          {canManagePromociones && (canUpdatePromocion || canDeletePromocion) && (
                            <li className="menu-divider" />
                          )}
                          {canManagePromociones && canUpdatePromocion && (
                            <li>
                              <button
                                type="button"
                                className="menu-item"
                                onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); abrirModalEditar(promo); }}
                              >
                                <i className="bi bi-pencil-square" />
                                Editar
                              </button>
                            </li>
                          )}
                          {canManagePromociones && canDeletePromocion && (
                            <li>
                              <button
                                type="button"
                                className="menu-item is-danger"
                                onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); abrirModalEliminar(promo); }}
                              >
                                <i className="bi bi-trash" />
                                Eliminar
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array.from({ length: emptyRows }).map((_, idx) => (
                <tr key={`promo-empty-${idx}`} className="promociones-table-empty-row" aria-hidden="true">
                  <td colSpan={9}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
